import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { FoodHeader } from '@cf-shared/data-model/food';
import { UiDisplayNutrient, uiDisplayNutrients } from 'models/display-nutrient';
import { splitRecentMeals } from './recent-meals-layout';

/**
 * The meal screen's empty state: the user's last meal in this slot, offered up front, and the
 * other recent meals listed under it (#4969). Since #5522 they are rows of one card; the
 * section's title and its View All button sit in the ingredient list's own header row.
 *
 * It only DRAWS them and says what the user tapped. Everything that changes the meal -- adding
 * the repeated foods, the undo, opening the portion screen, opening the full meals list -- stays
 * in `IngredientList`, which owns the ingredient list those actions change and the return
 * callback the meals list comes back to. Fetching the recent meals stays there too, so they
 * are fetched at the same moment they always were rather than only once the meal is empty.
 */
@Component({
  selector: 'cs-recent-meals-empty-state',
  templateUrl: 'recent-meals-empty-state.html',
  host: { 'class': 'recent-meals-empty-state' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CsRecentMealsEmptyStateComponent {
  protected LL = LL;

  @Input() recentMeals: FoodHeader[] = [];
  @Input() recentMealsLoading: boolean = false;
  @Input() currentMealName: string;

  /** the user pressed the up-front card's button: log that whole meal in one tap */
  @Output() repeatMeal: EventEmitter<FoodHeader> = new EventEmitter();
  /** the user picked one of the other recent meals: open it for confirmation */
  @Output() openMeal: EventEmitter<FoodHeader> = new EventEmitter();

  /**
   * The recent meal offered up front, and the ones listed under it.
   *
   * Both are WORKED OUT from the loaded list every time rather than stored beside it. The
   * meal the user is standing in is an input and the list is reloaded on its own schedule, so
   * a stored answer has two ways to go stale and a derived one has none. `splitRecentMeals`
   * costs a scan of at most a handful of entries, and this card only exists while the meal is
   * empty.
   */
  protected get repeatHero(): FoodHeader | undefined {
    return splitRecentMeals(this.recentMeals, this.currentMealName).hero;
  }

  protected get otherRecentMeals(): FoodHeader[] {
    return splitRecentMeals(this.recentMeals, this.currentMealName).others;
  }

  /** The macros drawn on the offered meal. Already totalled and formatted by the loader. */
  protected get repeatHeroNutrients(): UiDisplayNutrient[] {
    return uiDisplayNutrients(this.repeatHero?.displayNutrients);
  }

  protected onOpenMeal(meal: FoodHeader, event?: Event) {
    // the row's own add button sits inside the row, so without this one tap would open the meal twice
    event?.stopPropagation();
    this.openMeal.emit(meal);
  }
}
